import { KeyboardEvent, useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

export interface TagOption {
    /** The value stored on the profile. */
    value: string;
    /** What the therapist reads. Searched against, so already translated. */
    label: string;
}

interface TagPickerProps {
    options: TagOption[];
    selected: string[];
    max: number;
    /**
     * Receives the whole new selection, and is expected to both store and
     * persist it. It takes `next` rather than reading component state because a
     * caller that saved from its own state would save the selection as it was
     * one render ago — React has not applied the update yet at this point.
     */
    onChange: (next: string[]) => void;
    /**
     * Offers what the therapist typed as a new entry when nothing matches.
     * Off where the vocabulary is genuinely closed.
     */
    allowCustom?: boolean;
    /**
     * Whether the whole catalogue is listed before anything is typed.
     *
     * True where the picker owns the screen and browsing is the point — the
     * profile's own settings step. False in a form beside other fields, where
     * 28 chips stacked under the search box push everything after them off the
     * page; there the list appears once the therapist starts typing.
     */
    browsable?: boolean;
    /** Placeholder for the search field. */
    searchLabel: string;
    /** Accessible name for the search field. */
    inputLabel: string;
    emptyLabel: string;
}

/**
 * A long catalogue reduced to what the therapist is looking for.
 *
 * The two catalogues this replaces are 28 chips each, rendered as one flat
 * wrap. At that length the list stops being scannable — finding "sensorimotor"
 * means reading every entry — and the eight already chosen are lost among the
 * twenty that are not. So: chosen ones first and always visible, the rest
 * behind a search field.
 *
 * Selection is capped rather than merely discouraged. Past a handful of entries
 * a profile stops saying anything, and the cap is the honest way to say so.
 */
export default function TagPicker({
    options,
    selected,
    max,
    onChange,
    allowCustom = false,
    browsable = true,
    searchLabel,
    inputLabel,
    emptyLabel,
}: TagPickerProps) {
    const { t } = useTranslation();
    const [query, setQuery] = useState("");
    const listId = useId();

    const chosen = useMemo(
        () =>
            selected.map(
                (value) =>
                    options.find((option) => option.value === value) ?? {
                        value,
                        label: value,
                    },
            ),
        [options, selected],
    );

    const matches = useMemo(() => {
        const needle = normalize(query);
        if (!needle && !browsable) return [];
        return options
            .filter((option) => !selected.includes(option.value))
            .filter((option) => !needle || normalize(option.label).includes(needle));
    }, [options, selected, query, browsable]);

    const full = selected.length >= max;

    // Offered only when the catalogue genuinely has no word for it. Comparing
    // against the labels rather than the codes is what the therapist sees, and
    // is the comparison that stops "Anxiety" being added next to "Ansiedad".
    const typed = query.trim();
    const customCandidate = useMemo(() => {
        if (!allowCustom || !typed) return null;
        const needle = normalize(typed);
        const clashes = options.some((option) => normalize(option.label) === needle);
        const held = selected.some((value) => normalize(value) === needle);
        return clashes || held ? null : typed;
    }, [allowCustom, typed, options, selected]);

    const add = (value: string) => {
        if (full || selected.includes(value)) return;
        onChange([...selected, value]);
        setQuery("");
    };

    const remove = (value: string) => {
        onChange(selected.filter((entry) => entry !== value));
    };

    const onSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        // Enter takes the single remaining match, so a therapist who typed
        // enough to disambiguate never has to reach for the mouse.
        if (event.key !== "Enter") return;
        if (matches.length === 1) {
            event.preventDefault();
            add(matches[0].value);
            return;
        }
        if (matches.length === 0 && customCandidate) {
            event.preventDefault();
            add(customCandidate);
        }
    };

    return (
        <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-2">
                {chosen.length === 0 ? (
                    <p className="text-sm text-content-tertiary">{emptyLabel}</p>
                ) : (
                    chosen.map((option) => (
                        <span
                            key={option.value}
                            className="inline-flex items-center gap-1.5 rounded-full bg-accent-tint px-2.5 py-1 text-sm font-medium text-accent-on-tint"
                        >
                            {option.label}
                            <button
                                type="button"
                                onClick={() => remove(option.value)}
                                aria-label={t("publicDirectory.settings.picker.remove", {
                                    label: option.label,
                                })}
                                className="grid h-5 w-5 place-items-center rounded-full text-accent-on-tint/70 transition-colors duration-[--duration-fast] hover:bg-accent/15 hover:text-accent-on-tint focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent-text"
                            >
                                <svg
                                    aria-hidden="true"
                                    width="10"
                                    height="10"
                                    viewBox="0 0 10 10"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1.8"
                                    strokeLinecap="round"
                                >
                                    <path d="M1 1l8 8M9 1l-8 8" />
                                </svg>
                            </button>
                        </span>
                    ))
                )}
            </div>

            <div className="flex flex-col gap-2">
                <input
                    type="search"
                    value={query}
                    disabled={full}
                    onChange={(event) => setQuery(event.target.value)}
                    onKeyDown={onSearchKeyDown}
                    placeholder={searchLabel}
                    aria-label={inputLabel}
                    aria-controls={listId}
                    className="w-full rounded-md border border-border-strong bg-surface-raised px-4 py-2.5 text-sm text-content-primary placeholder:text-content-tertiary focus:border-accent focus:outline-2 focus:outline-offset-2 focus:outline-accent-text disabled:opacity-60 sm:max-w-sm"
                />

                {full ? (
                    <p className="text-sm text-content-secondary">
                        {t("publicDirectory.settings.picker.full", { max })}
                    </p>
                ) : (
                    <div id={listId} className="flex flex-wrap items-center gap-1.5">
                        {customCandidate && (
                            <button
                                type="button"
                                onClick={() => add(customCandidate)}
                                className="rounded-sm border border-dashed border-accent/40 bg-accent-tint px-2.5 py-1 text-sm font-medium text-accent-on-tint transition-colors duration-[--duration-fast] hover:bg-accent/15 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-text"
                            >
                                {t("publicDirectory.settings.picker.addCustom", {
                                    label: customCandidate,
                                })}
                            </button>
                        )}
                        {matches.length === 0 ? (
                            !customCandidate &&
                            (query.trim() ? (
                                <p className="text-sm text-content-tertiary">
                                    {t("publicDirectory.settings.picker.noMatches", {
                                        query,
                                    })}
                                </p>
                            ) : (
                                // Nothing typed and no browsing: say what the
                                // field wants rather than sitting empty.
                                !browsable && (
                                    <p className="text-sm text-content-tertiary">
                                        {t("publicDirectory.settings.picker.searchToAdd")}
                                    </p>
                                )
                            ))
                        ) : (
                            matches.map((option) => (
                                <button
                                    key={option.value}
                                    type="button"
                                    onClick={() => add(option.value)}
                                    className="rounded-sm border border-border-subtle px-2.5 py-1 text-sm text-content-secondary transition-colors duration-[--duration-fast] hover:border-border-strong hover:bg-surface-sunken hover:text-content-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-text"
                                >
                                    {option.label}
                                </button>
                            ))
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

/** Accent- and case-insensitive, so "adiccion" finds "Adicción". */
function normalize(value: string) {
    return value
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .trim();
}
